import { useEffect, useRef, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import type { ReactNode } from "react"
import type { Role } from "../api/types.ts"
import { useI18n } from "../i18n/index.tsx"
import { useSession } from "../auth/SessionProvider.tsx"
import { activeNavPath } from "../lib/nav-active.ts"
import { helpSlugFor } from "../lib/help-links.ts"
import { useTheme } from "../ui/theme.tsx"
import { CommandPalette } from "./CommandPalette.tsx"
import { NotificationBell } from "./NotificationBell.tsx"
import { Button, Drawer, Icon, IconButton, Select, type IconName } from "../ui/index.ts"

type NavItem = { to: string; labelKey: string; icon: IconName; roles: readonly Role[] }
type NavGroup = { labelKey: string | null; items: readonly NavItem[] }

const ALL: readonly Role[] = ["admin", "shipper", "warehouse"]

/**
 * 메뉴를 업무 흐름으로 묶는다. 역할에 없는 화면은 아예 보여 주지 않는다 —
 * 눌러 봐야 403 을 받을 뿐이다.
 */
const NAV: readonly NavGroup[] = [
  {
    labelKey: null,
    items: [{ to: "/", labelKey: "nav.dashboard", icon: "inbox", roles: ALL }],
  },
  {
    labelKey: "nav.group.work",
    items: [
      { to: "/cases", labelKey: "nav.cases", icon: "list", roles: ALL },
      { to: "/cases/new", labelKey: "nav.caseNew", icon: "plus", roles: ["admin", "shipper"] },
      { to: "/scan", labelKey: "nav.scan", icon: "scan", roles: ["admin", "warehouse"] },
      // 무적화물은 입고 스캔 화면에서도 처리하지만, 쌓인 상자를 되짚는 일은 따로 본다.
      // 화주에게는 보이지 않는다 — 어느 화주의 상자인지 아직 모르는 기록이다(서버도 막는다).
      { to: "/orphans", labelKey: "nav.orphans", icon: "alert", roles: ["admin", "warehouse"] },
      { to: "/shipments", labelKey: "nav.shipments", icon: "ship", roles: ALL },
    ],
  },
  {
    labelKey: "nav.group.master",
    items: [{ to: "/item-masters", labelKey: "nav.itemMasters", icon: "tag", roles: ALL }],
  },
  {
    labelKey: "nav.group.help",
    items: [{ to: "/manual", labelKey: "nav.manual", icon: "doc", roles: ALL }],
  },
  {
    labelKey: "nav.group.account",
    // 비밀번호 변경은 메뉴에 두지 않는다 — 1년에 한두 번 쓰는 길이라 늘 자리를 차지할
    // 이유가 없다. "내 계정" 화면 안의 카드로 들어간다(pages/SettingsPage.tsx).
    items: [{ to: "/settings", labelKey: "nav.settings", icon: "user", roles: ALL }],
  },
]

const NAV_ITEMS = NAV.flatMap((group) => group.items)
const NAV_PATHS = NAV_ITEMS.map((item) => item.to)

/** 지금 경로의 화면 이름. 헤더에 띄운다(좁은 화면에서는 사이드바가 없다). */
function titleKeyFor(pathname: string): string {
  if (/^\/cases\/\d+/.test(pathname)) return "caseDetail.title"
  if (/^\/shipments\/\d+/.test(pathname)) return "shipments.title"
  // 메뉴에 없는 화면(내 계정 안으로 들어간 비밀번호 변경)은 제 이름을 쓴다.
  if (pathname === "/settings/password") return "password.title"
  const active = activeNavPath(NAV_PATHS, pathname)
  return NAV_ITEMS.find((item) => item.to === active)?.labelKey ?? "app.title"
}

/** 지금 화면이 속한 메뉴 그룹. 없으면 null(대시보드처럼 묶이지 않은 화면). */
function groupKeyFor(pathname: string): string | null {
  const active = activeNavPath(NAV_PATHS, pathname)
  const group = NAV.find((candidate) => candidate.items.some((item) => item.to === active))
  return group?.labelKey ?? null
}

/**
 * 지금 어디에 있는지 한 줄. 상세 화면처럼 사이드바에 딱 맞는 항목이 없는 곳에서
 * "반품 목록 아래의 상세"임을 알려 준다.
 */
function Breadcrumb({ pathname, role }: { pathname: string; role: Role }) {
  const { t } = useI18n()
  // 이 화면을 설명하는 매뉴얼 문서(lib/help-links.ts). 없으면 링크도 없다.
  const help = helpSlugFor(pathname, role)
  if (pathname === "/" && help === null) return null

  const groupKey = groupKeyFor(pathname)
  const titleKey = titleKeyFor(pathname)

  return (
    <nav className="crumbs" aria-label={t("nav.menu")}>
      {pathname === "/" ? (
        <span className="crumbs__here">{t("nav.dashboard")}</span>
      ) : (
        <>
          <Link to="/">{t("nav.dashboard")}</Link>
          {groupKey ? (
            <>
              <Icon name="chevronRight" size={12} />
              <span>{t(groupKey)}</span>
            </>
          ) : null}
          <Icon name="chevronRight" size={12} />
          <span className="crumbs__here">{t(titleKey)}</span>
        </>
      )}

      {/* 이 화면에서 무엇을 하는지 설명하는 문서로 바로 간다. */}
      {help ? (
        <Link className="crumbs__help" to={`/manual/${help}`}>
          <Icon name="info" size={13} />
          {t("manual.help")}
        </Link>
      ) : null}
    </nav>
  )
}

function NavList({ role, onNavigate }: { role: Role; onNavigate?: () => void }) {
  const { t } = useI18n()
  const { pathname } = useLocation()
  // NavLink 는 링크마다 따로 판정해 /cases/new 에서 "반품 목록"까지 켠다. 메뉴 전체에서 하나만 고른다.
  const active = activeNavPath(NAV_PATHS, pathname)
  return (
    <>
      {NAV.map((group, index) => {
        const items = group.items.filter((item) => item.roles.includes(role))
        // 역할 필터로 항목이 전부 사라지면 그룹 제목도 그리지 않는다.
        if (items.length === 0) return null
        return (
          <div className="nav__group" key={group.labelKey ?? `group-${index}`}>
            {group.labelKey ? <div className="nav__group-label">{t(group.labelKey)}</div> : null}
            {items.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={onNavigate}
                className={item.to === active ? "nav__link is-active" : "nav__link"}
                aria-current={item.to === active ? "page" : undefined}
              >
                <Icon name={item.icon} size={17} className="nav__link-icon" />
                {t(item.labelKey)}
              </Link>
            ))}
          </div>
        )
      })}
    </>
  )
}

/** 헤더의 계정 드롭다운. 언어와 로그아웃을 여기 모은다. */
function AccountMenu() {
  const { t, locale, setLocale } = useI18n()
  const { user, logout } = useSession()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDetailsElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener("pointerdown", onPointerDown)
    return () => document.removeEventListener("pointerdown", onPointerDown)
  }, [open])

  if (!user) return null

  return (
    <details
      className="menu"
      ref={ref}
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="menu__button">
        <Icon name="user" size={16} />
        {/* 좁은 화면에서는 이름을 접는다 — 이름이 길면 머리 한 줄이 통째로 넘쳐
            화면이 가로로 밀린다(누구인지는 판을 열면 맨 위에 있다). */}
        <span className="menu__button-name">{user.name}</span>
        <Icon name="chevronDown" size={14} />
      </summary>
      <div className="menu__panel">
        <div className="menu__who">
          <strong>{user.name}</strong>
          {t(`role.${user.role}`)}
        </div>
        <Select
          value={locale}
          onChange={(event) => setLocale(event.target.value as "ko" | "ja")}
          aria-label={t("settings.language")}
        >
          <option value="ko">{t("settings.languageKo")}</option>
          <option value="ja">{t("settings.languageJa")}</option>
        </Select>
        <Button variant="ghost" icon="logout" onClick={() => void logout()}>
          {t("nav.logout")}
        </Button>
      </div>
    </details>
  )
}

export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useI18n()
  const { user } = useSession()
  const { theme, toggle } = useTheme()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)

  // 경로가 바뀌면 모바일 메뉴를 닫는다 — 항목을 누르고도 메뉴가 남아 있으면 안 된다.
  useEffect(() => setMenuOpen(false), [location.pathname])

  // ⌘K(맥) · Ctrl+K — 어느 화면에서든 검색을 연다. 칸에 글을 치는 중이어도 연다:
  // 이 조합을 쓰는 브라우저 기능(주소창 검색)보다 이쪽이 자주 쓰인다.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault()
        setSearchOpen(true)
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [])

  if (!user) return null

  return (
    <div className="shell">
      <nav className="shell__nav" aria-label={t("nav.menu")}>
        <div className="nav__brand">
          <span className="nav__brand-mark">
            <Icon name="box" size={16} />
          </span>
          {t("app.title")}
        </div>
        <NavList role={user.role} />
        <div className="nav__spacer" />
        <div className="nav__foot">
          <Icon name="user" size={16} />
          <span>
            <span className="nav__foot-name">{user.name}</span> · {t(`role.${user.role}`)}
          </span>
        </div>
      </nav>

      <div className="shell__main">
        <header className="shell__header">
          <IconButton
            icon="menu"
            label={t("nav.menu")}
            className="shell__menu-button"
            onClick={() => setMenuOpen(true)}
          />
          <div className="shell__title">{t(titleKeyFor(location.pathname))}</div>
          <div className="shell__grow" />

          {/* 넓은 화면에서는 무엇을 찾을 수 있는지까지 적어 둔다. 좁으면 아이콘만. */}
          <button type="button" className="search-trigger" onClick={() => setSearchOpen(true)}>
            <Icon name="search" size={15} />
            <span className="search-trigger__label">{t("search.placeholder")}</span>
            <kbd>⌘K</kbd>
          </button>
          <IconButton
            icon="search"
            label={t("search.title")}
            className="search-trigger__compact"
            onClick={() => setSearchOpen(true)}
          />

          <NotificationBell />

          <IconButton
            icon={theme === "dark" ? "sun" : "moon"}
            label={t(theme === "dark" ? "theme.toLight" : "theme.toDark")}
            onClick={toggle}
          />
          <AccountMenu />
        </header>
        <Breadcrumb pathname={location.pathname} role={user.role} />
        <main className="shell__content">{children}</main>
      </div>

      <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />

      {menuOpen ? (
        <Drawer
          title={t("app.title")}
          side="left"
          width="narrow"
          onClose={() => setMenuOpen(false)}
        >
          <nav className="shell__nav" aria-label={t("nav.menu")}>
            <NavList role={user.role} onNavigate={() => setMenuOpen(false)} />
          </nav>
        </Drawer>
      ) : null}
    </div>
  )
}
